import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext.jsx';
import { supabase } from '../supabase.js';
import {
  archiveNotifications, categoryLabels, getNotificationPreferences, listNotifications, markNotificationsRead,
  notificationTime, setNotificationPreference, setUnread, webPathFor,
} from './notifications.js';
import { SignInGate } from '../SignIn.jsx';

/** /notifications: the notification list and the settings for each kind of update. */
export default function NotificationsPage() {
  const { user, loading } = useAuth();
  if (loading) return <div className="content-wrap"><p>Loading account…</p></div>;
  if (!user || !supabase) return <SignInGate next="/notifications"/>;
  return <div className="content-wrap account-page" key={user.id}>
    <header className="coord-page-heading"><div><span className="account-kicker">NOTIFICATIONS</span><h1>Notifications</h1></div></header>
    <NotificationList client={supabase} />
    <Preferences client={supabase} />
  </div>;
}

export function NotificationList({ client }) {
  const navigate = useNavigate();
  const [rows, setRows] = useState(null), [hasMore, setHasMore] = useState(false), [error, setError] = useState(''), [actionError, setActionError] = useState('');
  const load = useCallback(() => listNotifications(client).then(page => { setRows(page.items); setHasMore(page.hasMore); setError(''); }).catch(cause => setError(cause.message)), [client]);
  useEffect(() => { void load(); }, [load]);

  const act = async (work) => { setActionError(''); try { await work(); } catch (cause) { setActionError(cause.message); } };
  const open = row => act(async () => {
    if (!row.read_at) { setUnread(await markNotificationsRead(client, [row.id])); }
    navigate(webPathFor(row.data));
  });
  const markAll = () => act(async () => {
    setUnread(await markNotificationsRead(client, null));
    const now = new Date().toISOString();
    setRows(existing => existing.map(row => row.read_at ? row : { ...row, read_at: now }));
  });
  const clearRead = () => act(async () => {
    const ids = rows.filter(row => row.read_at).map(row => row.id);
    setUnread(await archiveNotifications(client, ids));
    setRows(existing => existing.filter(row => !ids.includes(row.id)));
  });
  const more = () => act(async () => {
    const page = await listNotifications(client, { before: rows[rows.length - 1] });
    setRows(existing => [...existing, ...page.items.filter(item => !existing.some(row => row.id === item.id))]);
    setHasMore(page.hasMore);
  });

  if (error) return <div role="alert"><p>{error}</p><button type="button" className="button button-secondary" onClick={load}>Try again</button></div>;
  if (rows === null) return <p>Loading notifications…</p>;
  const unread = rows.filter(row => !row.read_at).length;
  return <section className="account-content-panel" aria-label="Your notifications">
    {rows.length === 0 ? <p>No notifications yet. We will let you know about new bids, messages, visits and payments.</p> : <>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
        {unread > 0 && <button type="button" className="button button-secondary" onClick={markAll}>Mark all as read</button>}
        {rows.some(row => row.read_at) && <button type="button" className="button button-secondary" onClick={clearRead}>Clear read notifications</button>}
      </div>
      <ul style={{ listStyle: 'none', padding: 0, margin: '12px 0 0', display: 'grid', gap: 4 }}>{rows.map(row => <li key={row.id}>
        <button type="button" onClick={() => open(row)} aria-label={`${row.read_at ? '' : 'Unread. '}${row.title}. ${row.body}`}
          style={{ width: '100%', textAlign: 'left', padding: '12px 8px', border: 0, borderBottom: '1px solid var(--line)', background: 'transparent', color: 'inherit', cursor: 'pointer', display: 'grid', gap: 2 }}>
          <span style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {!row.read_at && <span className="status-pill">New</span>}
            <strong style={{ fontWeight: row.read_at ? 500 : 700 }}>{row.title}</strong>
          </span>
          <small>{[row.body, notificationTime(row.created_at)].filter(Boolean).join(' · ')}</small>
        </button>
      </li>)}</ul>
      {hasMore && <p><button type="button" className="button button-secondary" onClick={more}>Show older</button></p>}
    </>}
    {actionError && <p role="alert">{actionError}</p>}
  </section>;
}

export function Preferences({ client }) {
  const [prefs, setPrefs] = useState(null), [error, setError] = useState(''), [saveError, setSaveError] = useState('');
  useEffect(() => { getNotificationPreferences(client).then(setPrefs).catch(cause => setError(cause.message)); }, [client]);
  const toggle = async (pref, channel, value) => {
    const previous = prefs;
    const next = { ...pref, [channel]: value };
    setSaveError('');
    setPrefs(rows => rows.map(row => row.category === pref.category ? next : row));
    try { setPrefs(await setNotificationPreference(client, next)); }
    catch (cause) { setPrefs(previous); setSaveError(cause.message); }
  };
  return <section className="account-content-panel" aria-labelledby="notification-settings-heading" style={{ marginTop: 24 }}>
    <h2 id="notification-settings-heading">Notification settings</h2>
    <p>Push and email never include addresses, message text or amounts. Open Resident Service 360 to see the details. Push goes to the Resident Service 360 iPhone app.</p>
    {error ? <p role="alert">{error}</p> : prefs === null ? <p>Loading settings…</p> : <table style={{ width: '100%', borderCollapse: 'collapse' }}>
      <thead><tr><th scope="col" style={{ textAlign: 'left' }}>Kind of update</th><th scope="col">In the app</th><th scope="col">Push</th><th scope="col">Email</th></tr></thead>
      <tbody>{prefs.map(pref => {
        const [title, detail] = categoryLabels[pref.category] || [pref.category, ''];
        return <tr key={pref.category} style={{ borderTop: '1px solid var(--line)' }}>
          <th scope="row" style={{ textAlign: 'left', padding: '10px 0', fontWeight: 500 }}>{title}<br /><small>{detail}</small></th>
          {['in_app', 'push', 'email'].map(channel => <td key={channel} style={{ textAlign: 'center' }}>
            <input type="checkbox" checked={Boolean(pref[channel])} disabled={pref.locked}
              aria-label={`${title}: ${channel === 'in_app' ? 'In the app' : channel === 'push' ? 'Push' : 'Email'}`}
              onChange={event => void toggle(pref, channel, event.target.checked)} />
          </td>)}
        </tr>;
      })}</tbody>
    </table>}
    {saveError && <p role="alert">{saveError}</p>}
  </section>;
}
